import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { PlanCard, TrialCard } from "@/components/payments/PlanCard";
import { SubscriptionStatus } from "@/components/payments/SubscriptionStatus";
import { PaymentHistory } from "@/components/payments/PaymentHistory";
import { api, errMsg } from "@/lib/api";

const TestNote = ({ info }) => (
  <p className="text-xs rounded-xl bg-amber-500/10 text-amber-300 p-3" data-testid="payments-test-note">
    Test mode — no real money moves. {info.international
      ? "International test cards: Visa 4012 8888 8888 1881 or Mastercard 5555 5555 5555 4444, any future date, any CVV."
      : "The UPI QR is a test QR (real GPay/PhonePe say \u201cinvalid UPI ID\u201d). Test card: 4718 6091 0820 4366, any future date, CVV 123, OTP 123456."}
  </p>
);

export default function Upgrade() {
  usePageMeta({ title: "Plans & Pricing" });
  const [info, setInfo] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/plans"), api.get("/usage")]).then(([p, u]) => { setInfo(p.data); setUsage(u.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!info || !usage) return <div className="min-h-screen"><Header /><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></div>;
  const active = usage.plan === "premium";

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-10" data-testid="upgrade-page">
        <div className="grid lg:grid-cols-[1fr_380px] gap-8 items-start">
          <div className="space-y-3">
            <div className="eyebrow">Plans</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">Never run out of rizz</h1>
            <p className="text-sm sm:text-base text-muted-foreground max-w-xl">
              Start with {info.trial.total} free generations. Then pick a plan — every reply, Scan and Photo Rizz uses one credit, and failed generations are never charged.
              {active && " Buying again adds the new credits and days on top of your current plan."}
            </p>
            {info.test_mode && info.enabled && <span data-testid="payments-test-mode" className="inline-block text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 bg-amber-500/15 text-amber-400">Test mode</span>}
          </div>
          <SubscriptionStatus usage={usage} />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 pt-3" data-testid="plans-grid">
          <TrialCard total={info.trial.total} usage={usage} />
          {info.plans.map((p) => <PlanCard key={p.plan_id} plan={p} info={info} busy={busy} setBusy={setBusy} active={active} />)}
        </div>

        <div className="space-y-3 max-w-3xl">
          {!info.enabled && (
            <p className="text-sm text-muted-foreground" data-testid="payments-disabled-note">
              {info.international && !info.international_enabled
                ? "International card payments are coming soon. Prices are shown in USD for customers outside India."
                : "Online payments aren't switched on yet."}
            </p>
          )}
          {info.enabled && info.test_mode && <TestNote info={info} />}
          <p className="text-[11px] text-muted-foreground" data-testid="pricing-region-note">
            Prices in {info.currency} for {info.international ? "customers outside India" : "India"}, based on your profile country.
            {info.international ? " Paid by international Visa / Mastercard via Razorpay; your bank converts the amount." : " Pay with UPI, debit/credit cards or netbanking via Razorpay."}
          </p>
        </div>

        <PaymentHistory />
        <Link to="/app" data-testid="upgrade-back-link" className="text-sm text-primary">Back to the generator</Link>
      </main>
    </div>
  );
}
